import { useCallback, useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate } from "@/lib/roles";
import { AppShell } from "@/components/app/AppShell";

const TYPE_LABELS = { know: "Know", portability: "Portable copy", delete: "Delete", correct: "Correct", opt_out: "Opt out", limit: "Limit sensitive" };
const STATUSES = { received: "Received", verifying: "Verifying identity", in_progress: "In progress", completed: "Completed", denied: "Denied" };
const selectCls = "h-9 rounded-md border border-gray-200 bg-white px-2 text-sm text-oath-ink";

const daysLeft = (due) => Math.ceil((new Date(due) - Date.now()) / 86400000);

const DueBadge = ({ row }) => {
  if (["completed", "denied"].includes(row.status)) return <span className="text-xs text-oath-muted">—</span>;
  const d = daysLeft(row.due_at);
  const cls = d < 0 ? "bg-red-50 text-red-700" : d <= 10 ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700";
  return <span className={`rounded-full px-2 py-0.5 text-[11px] ${cls}`} data-testid={`privacy-due-${row.id}`}>{d < 0 ? `${-d}d overdue` : `${d}d left`}</span>;
};

const StatusSelect = ({ row, onDone }) => {
  const change = async (e) => {
    try {
      await api.post(`/admin/privacy-requests/${row.id}/status`, { status: e.target.value });
      toast.success(`${row.reference} updated`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <select value={row.status} onChange={change} className={selectCls} data-testid={`privacy-status-${row.id}`}>
      {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
    </select>
  );
};

export default function AdminPrivacy() {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/admin/privacy-requests?status=${status}`);
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setRows([]);
    }
  }, [status]);
  useEffect(() => { load(); }, [load]);

  return (
    <AppShell eyebrow="— SUPER ADMIN" title="Privacy requests">
      <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-gray-200 bg-white px-3 text-sm" data-testid="privacy-status-filter">
        <option value="">All statuses</option>
        {Object.entries(STATUSES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-gray-200 bg-white">
        <table className="w-full min-w-[960px] text-left text-sm" data-testid="privacy-table">
          <thead className="border-b border-gray-200 bg-oath-bg/60 font-mono-label text-[9px] text-oath-muted">
            <tr><th className="px-4 py-3">REFERENCE</th><th className="px-4 py-3">REQUESTER</th><th className="px-4 py-3">REQUEST</th><th className="px-4 py-3">DETAILS</th><th className="px-4 py-3">RECEIVED</th><th className="px-4 py-3">DUE</th><th className="px-4 py-3">STATUS</th></tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={7} className="px-4 py-8"><Loader2 className="h-5 w-5 animate-spin text-oath-muted" /></td></tr>}
            {rows?.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-oath-muted" data-testid="privacy-empty">No privacy requests yet.</td></tr>}
            {rows?.map((r) => (
              <tr key={r.id} className="border-b border-gray-100 align-top last:border-0" data-testid={`privacy-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-xs text-oath-ink">{r.reference}<div className="mt-1 text-[10px] text-oath-muted">{r.source === "california" ? "CA page" : "Subject rights"}</div></td>
                <td className="px-4 py-3 text-oath-secondary">
                  <div className="font-medium text-oath-ink">{r.full_name}</div><div>{r.email}</div><div className="font-mono text-xs">{r.phone}</div>
                  {r.requester === "agent" && <div className="mt-1 text-xs text-amber-700">Agent: {r.agent_name}</div>}
                  {r.california_resident && <div className="mt-1 font-mono-label text-[9px] text-oath-accent">CA RESIDENT</div>}
                </td>
                <td className="px-4 py-3"><div className="flex flex-wrap gap-1">{r.request_types.map((t) => <span key={t} className="rounded-full bg-oath-accent-light px-2 py-0.5 text-[11px] text-oath-accent">{TYPE_LABELS[t]}</span>)}</div></td>
                <td className="max-w-xs px-4 py-3 text-xs text-oath-secondary">{r.report_id && <div className="font-mono">ID: {r.report_id}</div>}{r.details}</td>
                <td className="px-4 py-3 font-mono text-xs text-oath-secondary">{fmtDate(r.created_at)}</td>
                <td className="px-4 py-3"><DueBadge row={r} /></td>
                <td className="px-4 py-3"><StatusSelect row={r} onDone={load} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
